import { useEffect } from 'react';

import { useLoaderData, useOutletContext, useRouteLoaderData } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';
import type { StaticPageLoaderData } from '~/services/static_page';

import { useNonRevalidatingFetcher } from '../useNonRevalidatingFetcher';

interface Response {
  isLoading: boolean;
  mutateLikes: (postId: string) => void;
  submitLike: (postId: string, subscriptionId?: string, adminId?: string) => void;
}

interface Props {
  onSuccess?: (postId: string) => void;
}

const useLikePost = ({ onSuccess }: Props): Response => {
  const fetcher = useNonRevalidatingFetcher();

  const isLoading = fetcher.state === 'submitting';

  const { handleOpenModal } = useGlobalModals();

  const { admin } = useOutletContext<OutletContext>();
  const { subscription, isLoggedIn } = useRouteLoaderData('root') as RootLoaderData;
  const { hasLikedPostsDataFetched, hasPostInsightsDataFetched } = useLoaderData<StaticPageLoaderData>();
  const isAdminLoggedIn = Boolean(admin);
  const isNotLoggedIn = !isAdminLoggedIn && !isLoggedIn;

  const submitLike = async (postId: string, subscriptionId?: string, adminId?: string) => {
    const formData = new FormData();
    formData.set('post_id', postId);

    if (adminId) {
      formData.set('user_id', adminId);
      await fetcher.submit(formData, {
        method: 'post',
        action: '/admins/post_like',
      });
    } else if (subscriptionId) {
      formData.set('subscription_id', subscriptionId);
      await fetcher.submit(formData, { method: 'post', action: '/post_like' });
    }
  };

  const mutateLikes = (postId: string) => {
    if (!postId || !hasLikedPostsDataFetched || !hasPostInsightsDataFetched) {
      return;
    }

    if (isNotLoggedIn) {
      handleOpenModal(
        'login',
        { preventScrollReset: true },
        { after_login_action_post_id: postId, after_login_action: 'like' },
      );
      return;
    }

    submitLike(postId, String(subscription?.id), isAdminLoggedIn ? admin?.id : undefined);
  };

  const fetcherData = fetcher.data as { ok?: boolean; post_id?: string };
  const isSuccess = fetcherData?.ok === true;
  useEffect(() => {
    if (isSuccess && fetcherData.post_id) {
      onSuccess?.(fetcherData.post_id);
    }
  }, [isSuccess, fetcherData]);

  return {
    isLoading,
    mutateLikes,
    submitLike,
  };
};

export default useLikePost;
